/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {Sandbox} from '@adobe/exc-app/user';

/**
 * Gets an enabled sandbox object from the list of available sandboxes for a
 * user, using the `enabledSandboxes` value as the source of truth for whether
 * the sandboxes are enabled.
 * @param {Sandbox[]} sandboxes List of available sandboxes for a user.
 * @param {string} enabledSandboxes Comma separated list of sandboxes enabled
 *   for the current solution.
 * @returns {Sandbox=} The default sandbox if it is enabled or the first in the
 *   list. If nothing is found, returns undefined.
 */
export const getEnabledSandbox = (
  sandboxes: Sandbox[],
  enabledSandboxes?: string,
  selectedSandbox?: Sandbox
): Sandbox | null => {
  const enabledSandboxNames = (enabledSandboxes || '').split(',').filter(name => name !== 'OFF');
  const onlyEnabled = enabledSandboxNames.includes('ALL') ?
    sandboxes :
    sandboxes.filter(({name}) => enabledSandboxNames.indexOf(name) > -1);
  // Try to find the selected sandbox in the list first, then pick the default
  // sandbox, then the first in the list.
  return (selectedSandbox && onlyEnabled.find(({name}) => name === selectedSandbox.name)) ||
    onlyEnabled.find(sb => sb.isDefault) ||
    onlyEnabled[0];
};
